import './address.scss';
import 'react-calendar/dist/Calendar.css';

import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import AskCpfModal from '@/resources/components/askCpfModal/AskCpfModal';
import { useShipmentContext } from '@/resources/components/cart/shipment/Shipment';
import { useCartContext } from '@/resources/hooks/useCartContext';
import { useWebContext } from '@/resources/hooks/useWebContext';
import { setShipmentAddress } from '@/routes/order';
import { getOrderMinValue } from '@/services/global';
import { ApplicationState } from '@/store';

import { IOrder } from '../../../../../store/ducks/order/types';
import AddressModal from '../../../global/address/AddressModal';
import { IAddressModalStep } from '../../../global/address/components/types';
import FooterDefaultButton from '../footerDefaultButton/FooterDefaultButton';
import { ShipmentCard } from '../shipmentCard/ShipmentCard';
import DeliveryDiscountAlert from './components/delivery/DeliveryDiscountAlert';
import DeliveryInformation from './components/delivery/DeliveryInformation';
import RenderOrderAddress from './components/RenderOrderAddress';
import RenderOrderType from './components/RenderOrderType';

interface Props {
  order: IOrder;
}

function Address({ order }: Props) {
  const { shipment, shop_name_dst, id } = order;
  const { step, setStep } = useShipmentContext();
  const { configs } = useWebContext();
  const { setMinOrderAmount, setWebappOutOfDelivery, webapp_out_of_delivery, min_order_amount } =
    useCartContext();

  const isOverMinOrder = order.cart.total > getOrderMinValue(order, min_order_amount);

  const [show, setShowAddress] = useState<boolean>(false);
  const [modalStep, setModalStep] = useState<IAddressModalStep>('type');
  const [showCpf, setShowCpf] = useState<boolean>(false);

  const [calcButtonDisabled, setCalcButtonDisabled] = useState<boolean>(false);

  const selectCPFAnswered = useSelector<ApplicationState, boolean>(
    (state) => state.client.cpfAnswered,
  );

  const handleChooseSchedule = () => {
    setModalStep('scheduling');
    setShowAddress(true);
  };

  const setShow = (show: boolean) => {
    if (!show) {
      setShowCpf(false);
      setShowAddress(show);

      return;
    }

    if (configs.ask_cpf && !selectCPFAnswered && !order?.client.cpf) {
      setShowCpf(true);
      return;
    }

    setShowAddress(show);
  };

  const handleRefreshShipment = () => {
    setCalcButtonDisabled(true);

    setShipmentAddress(id, shipment.address.id, { type: 'delivery' })
      .then((order) => {
        setMinOrderAmount(order?.shipment?.min_order_amount ?? 0);

        setWebappOutOfDelivery(!!order?.shipment?.webapp_out_of_delivery);
      })
      .catch((err) => {
        console.error(err);
      })
      .finally(() => setCalcButtonDisabled(false));
  };

  const showPickupAt = shipment.type === 'shop' || shipment.delivery?.type === 'google-maps';

  useEffect(() => {
    if (step === 2 && shipment.pickup_at !== null) setStep(3);
  }, [shipment.pickup_at, step, setStep]);

  const shouldRefreshCarrier =
    !!shipment.refresh_carrier &&
    (!!shipment.pickup_at || !showPickupAt) &&
    shipment.address &&
    isOverMinOrder;

  return (
    <>
      <ShipmentCard
        showModal={show}
        disabled={step <= 1 || order?.client?.type === 3}
        setShowModal={setShow}
        warningType="forma de entrega"
        handleCloseModal={() => setModalStep('type')}
      >
        <ShipmentCard.Content
          title="2 - Entrega"
          actions={!!shipment.address}
          footer={
            <>
              {!shipment.address && (
                <FooterDefaultButton title="Definir entrega" showModal={setShow} />
              )}

              {((shipment.type === 'delivery' && shipment.address) || webapp_out_of_delivery) && (
                <DeliveryInformation />
              )}

              {!shipment.pickup_at && showPickupAt && isOverMinOrder && (
                <FooterDefaultButton
                  title="Definir horário"
                  showModal={setShow}
                  onClick={() => handleChooseSchedule()}
                />
              )}

              {shouldRefreshCarrier && (
                <FooterDefaultButton
                  title="Calcular entrega"
                  onClick={handleRefreshShipment}
                  isDisabled={calcButtonDisabled}
                />
              )}
            </>
          }
        >
          <div className="d-flex flex-column">
            {shipment.type && !webapp_out_of_delivery && <RenderOrderType type={shipment.type} />}

            {shipment.address && (
              <RenderOrderAddress
                type={shipment.type}
                address={shipment.address}
                shopNameDst={shop_name_dst}
                pickupAt={shipment.pickup_at}
              />
            )}
          </div>
        </ShipmentCard.Content>

        <AddressModal
          showModal={show}
          step={modalStep}
          showSchedule={true}
          setShowModal={setShow}
          handleClose={() => setModalStep('type')}
        />

        <AskCpfModal show={showCpf} setShow={setShowCpf} setAddress={setShowAddress} />
      </ShipmentCard>

      <DeliveryDiscountAlert />
    </>
  );
}

export default Address;
